import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import type { KpiSalesFunnel, KpiStatusKey } from "../api";
import {
  FunnelSankey,
  type FunnelLink,
  type FunnelNode,
  type FunnelTone,
} from "./FunnelSankey";

export interface DealFlowData {
  funnel: KpiSalesFunnel;
  source?: string;
}

const STAGES = ["Source", "Qualified", "Outcome", "Contract", "Customer status"];

// Step 6 status nodes — fixed display order (good → neutral → bad, so flows
// read top-to-bottom) and tone. Rendered only when the cohort has deals in
// that status, so the chart stays clean across windows (a recent cohort has
// no Churned yet; most cohorts never have Managed-by-Reseller or M&A).
const STATUS_NODES: { key: KpiStatusKey; label: string; tone: FunnelTone }[] = [
  { key: "active", label: "Active", tone: "brand" },
  { key: "in_onboarding", label: "In Onboarding", tone: "brand" },
  { key: "managed_by_reseller", label: "Managed by Reseller", tone: "neutral" },
  { key: "m_and_a", label: "M&A", tone: "neutral" },
  { key: "other", label: "Other", tone: "neutral" },
  { key: "de_booked", label: "De-Booked", tone: "danger" },
  { key: "churned", label: "Churned", tone: "danger" },
];

/** Map the v7 SQO-cohort funnel to sankey nodes + links.
 *
 *   New | Existing → SQO → Won | Lost | Still Open → Booked | Not Booked
 *   → current customer status (Won deals only; both chains feed in).
 *
 * SQO always renders; every other node/link is shown only when it carries
 * volume, so terminal stages (Lost, Still Open) simply stop and immature
 * cohorts collapse gracefully rather than drawing empty boxes. */
function buildTopology(f: KpiSalesFunnel): { nodes: FunnelNode[]; links: FunnelLink[] } {
  const nodes: FunnelNode[] = [];
  const add = (n: FunnelNode) => {
    if (n.value > 0 || n.id === "sqo") nodes.push(n);
  };

  add({ id: "new", label: "New", sublabel: "qualified same month", value: f.new, col: 0, tone: "brand" });
  add({ id: "existing", label: "Existing", sublabel: "aged into SQO", value: f.existing, col: 0, tone: "brand" });
  add({ id: "sqo", label: "SQO", value: f.sqo, col: 1, tone: "brand" });
  add({ id: "won", label: "Won", value: f.won, col: 2, tone: "brand" });
  add({ id: "still_open", label: "Still Open", value: f.still_open, col: 2, tone: "warning" });
  add({ id: "lost", label: "Lost", value: f.lost, col: 2, tone: "danger" });
  add({ id: "booked", label: "Booked", sublabel: "chain complete", value: f.booked, col: 3, tone: "brand" });
  add({ id: "not_booked", label: "Not Booked", sublabel: "chain incomplete", value: f.not_booked, col: 3, tone: "warning" });
  for (const s of STATUS_NODES) {
    add({ id: `st_${s.key}`, label: s.label, value: f.status[s.key], col: 4, tone: s.tone });
  }

  const present = new Set(nodes.map((n) => n.id));
  const links: FunnelLink[] = [];
  const link = (from: string, to: string, value: number, tone: FunnelTone) => {
    if (value > 0 && present.has(from) && present.has(to)) {
      links.push({ from, to, value, tone });
    }
  };

  link("new", "sqo", f.new, "brand");
  link("existing", "sqo", f.existing, "brand");
  link("sqo", "won", f.won, "brand");
  link("sqo", "still_open", f.still_open, "warning");
  link("sqo", "lost", f.lost, "danger");
  link("won", "booked", f.booked, "brand");
  link("won", "not_booked", f.not_booked, "warning");
  for (const s of STATUS_NODES) {
    link("booked", `st_${s.key}`, f.status_by_chain.booked[s.key], s.tone);
    link("not_booked", `st_${s.key}`, f.status_by_chain.not_booked[s.key], s.tone);
  }

  return { nodes, links };
}

/** Sales funnel sankey: a cohort of SQO'd deals traced through outcome,
 *  contract chain, and current customer status. Deal-grain, mass-conserving —
 *  not an in-period stage count. Driven by the revops v7 topology. */
export function DealFlowSankey({
  periodLabel,
  data,
  onNodeClick,
}: {
  periodLabel: string;
  data: DealFlowData | null;
  onNodeClick?: (id: string) => void;
}) {
  const f = data?.funnel ?? null;
  const { nodes, links } = f
    ? buildTopology(f)
    : { nodes: [] as FunnelNode[], links: [] as FunnelLink[] };

  // A cohort whose deals are mostly unresolved (still open, or won-but-still-
  // onboarding) hasn't aged enough for the Won outcomes and Status fan to
  // mean much — flag it rather than letting it read as a flat funnel.
  const maturing =
    f != null &&
    f.cohort_total > 0 &&
    f.still_open + f.status.in_onboarding > f.cohort_total * 0.5;

  return (
    <Card className="col-span-4">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <CardTitle className="text-sm">
            Sales Funnel — SQO Cohort to Customer Status · {periodLabel}
          </CardTitle>
          <div className="text-[10px] text-muted-foreground">
            {data?.source ?? "HUBSPOT_DEALS · BILLING_CONTRACTS · HUBSPOT_COMPANIES"}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {f ? (
          <FunnelSankey
            nodes={nodes}
            links={links}
            stages={STAGES}
            ariaLabel="Sales funnel from new or existing deals through SQO, won, lost or still open, booked or not booked, to current customer status"
            onNodeClick={onNodeClick}
          />
        ) : (
          <div className="flex h-64 animate-pulse items-center justify-center rounded-md border border-dashed border-border bg-surface-raised/50 text-sm text-muted-foreground">
            Loading sales funnel…
          </div>
        )}
        <div className="mt-3 text-[10px] leading-relaxed text-muted-foreground">
          One row per SQO'd deal (Sales + SMB pipelines) whose SQO date falls in
          the period, followed through its own outcome — not an in-period stage
          count. Band widths are √-scaled; labels show actual deal counts.
          Booked = full contract chain wired (contract · PandaDoc · account ·
          deal · QuickBooks). Status = company's current snapshot, inherited
          onto Won deals.
          {maturing && (
            <span className="font-semibold text-warning">
              {" "}
              Recent cohort — most deals are still open or onboarding, so Won
              outcomes and customer status are still maturing.
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
